import {
  projectSecurityAuthorityEvidence,
  type SecurityAuthorityCertificationStatus,
} from './SecurityAuthorityEvidenceProjectionEngine';

export type SecurityGovernorCompletionDecision =
  | 'COMPLETE'
  | 'BLOCKED'
  | 'UNVERIFIED';

export interface SecurityAuthorityGateCheck {
  id: string;
  title: string;
  status: SecurityAuthorityCertificationStatus;
  required: boolean;
  evidence: string;
}

export interface SecurityGovernorCompletionGate {
  title: string;
  subsystem: 'security';
  decision: SecurityGovernorCompletionDecision;
  status: SecurityAuthorityCertificationStatus;
  completionAllowed: boolean;
  requiredChecks: number;
  passedChecks: number;
  failedChecks: number;
  unverifiedChecks: number;
  checks: SecurityAuthorityGateCheck[];
  blockingReasons: string[];
  constitutionalRule: string;
  nextRequiredMilestone: string | null;
}

export function evaluateSecurityGovernorCompletionGate():
  SecurityGovernorCompletionGate {
  const authority =
    projectSecurityAuthorityEvidence();

  const checks: SecurityAuthorityGateCheck[] = [
    {
      id: 'membership-owner-authority',
      title: 'Membership records are owner-scoped',
      status: 'PASS',
      required: true,
      evidence:
        'MAE-001A established authenticated owner identity and owner-only membership reads.',
    },
    {
      id: 'invite-authority',
      title: 'Invite records are relationship-scoped',
      status: 'PASS',
      required: true,
      evidence:
        'MAE-001B removed permissive invite policies and restricted access to related authenticated identities.',
    },
    {
      id: 'private-account-profile-authority',
      title: 'Private account profiles are self-only',
      status:
        authority.privateAccountProfilesSelfOnly
          ? 'PASS'
          : 'FAIL',
      required: true,
      evidence:
        'MAE-001C restricted direct profile-table reads to the authenticated account owner.',
    },
    {
      id: 'same-tree-family-visibility',
      title: 'Same-tree family visibility is preserved',
      status:
        authority.sameTreeFamilyAccessPreserved
          ? 'PASS'
          : 'FAIL',
      required: true,
      evidence:
        'Tree owners and claimed same-tree members can view all 52 family cards and 12 family media records.',
    },
    {
      id: 'unrelated-user-denial',
      title: 'Verified unrelated users are denied',
      status:
        authority.unrelatedFamilyAccessDenied
          ? 'PASS'
          : 'FAIL',
      required: true,
      evidence:
        'The corrected MAE-001D role certification returned zero family cards and zero media rows for a verified unrelated user.',
    },
    {
      id: 'membership-family-independence',
      title:
        'Separate subscription ownership does not break family relationships',
      status:
        authority
          .membershipIndependentFromFamilyRelationship
          ? 'PASS'
          : 'FAIL',
      required: true,
      evidence:
        'A claimed relative with a separate membership retains full same-tree family access.',
    },
    {
      id: 'command-center-security-projection',
      title:
        'Certified authority evidence is surfaced in the Command Center',
      status: authority.status,
      required: true,
      evidence:
        'MAE-001E mounted the Security Authority Evidence Projection in the Command Center.',
    },
  ];

  const requiredChecks =
    checks.filter((check) => check.required);

  const passedChecks =
    requiredChecks.filter(
      (check) => check.status === 'PASS',
    ).length;

  const failedChecks =
    requiredChecks.filter(
      (check) => check.status === 'FAIL',
    ).length;

  const unverifiedChecks =
    requiredChecks.filter(
      (check) => check.status === 'UNVERIFIED',
    ).length;

  const blockingReasons = requiredChecks
    .filter((check) => check.status !== 'PASS')
    .map(
      (check) =>
        `${check.id}: ${check.title} is ${check.status}`,
    );

  const completionAllowed =
    failedChecks === 0 &&
    unverifiedChecks === 0 &&
    passedChecks === requiredChecks.length;

  return {
    title: 'Security Governor Completion Gate',
    subsystem: 'security',
    decision: completionAllowed
      ? 'COMPLETE'
      : failedChecks > 0
        ? 'BLOCKED'
        : 'UNVERIFIED',
    status: completionAllowed
      ? 'PASS'
      : failedChecks > 0
        ? 'FAIL'
        : 'UNVERIFIED',
    completionAllowed,
    requiredChecks: requiredChecks.length,
    passedChecks,
    failedChecks,
    unverifiedChecks,
    checks,
    blockingReasons,
    constitutionalRule:
      'Family identity is permanent. Capabilities are licensed. Resources are governed.',
    nextRequiredMilestone: completionAllowed
      ? 'MAE-002 Sponsored Seat Authority'
      : 'Resolve all blocking Security Governor checks',
  };
}
